<!-- Outer modal: AG-Grid list of Channels + Add / Done. z-50 so it
     stacks on top of the Scanner edit modal that opened it. -->
<div *ngIf="showModal"
     class="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
     (click)="onBackdropClick($event, 'outer')">
  <div class="bg-white dark:bg-app-black-500 rounded shadow-lg w-[1100px] max-w-[95vw] max-h-[90vh] flex flex-col border border-gray-200 dark:border-[#434863]"
       (click)="$event.stopPropagation()">
    <div class="py-3 px-6 dark:bg-app-black-100 rounded-t flex items-center justify-between gap-3">
      <h2 class="text-sm font-semibold text-gray-900 dark:text-white">
        <i class="fa-solid fa-list-ul text-app-teal-100 mr-2"></i>
        Channels editor
        <span *ngIf="scannerId" class="text-[11px] text-gray-500 dark:text-gray-400 ml-2 font-mono">{{ scannerId }}</span>
      </h2>
      <div class="flex items-center gap-2">
        <button type="button" (click)="openAdd()"
                class="text-xs uppercase tracking-wide text-app-teal-100 hover:text-app-teal-200 border border-app-teal-100/60 hover:border-app-teal-100 rounded px-3 py-1 flex items-center gap-1">
          <i class="fa-solid fa-plus text-[10px]"></i>
          Add channel
        </button>
        <button type="button" (click)="onDone()"
                class="text-xs uppercase tracking-wide text-gray-500 hover:text-gray-300 border border-gray-300 dark:border-[#434863] hover:border-gray-400 rounded px-3 py-1">
          Done
        </button>
      </div>
    </div>

    <div class="flex-1 overflow-hidden px-6 py-4 flex flex-col gap-3">
      <p *ngIf="entries.length === 0"
         class="text-xs text-gray-500 dark:text-gray-400 italic">
        No channels. Click <strong>Add channel</strong> to define a channel on this scanner.
      </p>

      <div *ngIf="entries.length > 0" class="flex flex-col gap-1">
        <ag-grid-angular
          class="w-full h-[400px]"
          [theme]="gridTheme"
          [gridOptions]="gridOptions"
          [rowData]="entries"
          (rowClicked)="onRowClicked($event)"
          (rowDragEnd)="onRowDragEnd($event)">
        </ag-grid-angular>
        <p class="text-[10px] text-gray-500 dark:text-gray-400 italic">
          Click any row to edit · drag the <i class="fa-solid fa-grip-vertical"></i> handle to reorder · remove inside the edit popup.
          <span class="ml-2 text-amber-500">Changing an id orphans existing user subscriptions for that channel.</span>
        </p>
      </div>
    </div>
  </div>
</div>

<!-- Sub-modal: single-channel form. z-60 above the outer list. -->
<div *ngIf="showModal && editingIndex !== null"
     class="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4"
     (click)="onBackdropClick($event, 'inner')">
  <div class="bg-white dark:bg-app-black-500 rounded shadow-lg w-[560px] max-w-[95vw] max-h-[90vh] flex flex-col border border-gray-200 dark:border-[#434863]"
       (click)="$event.stopPropagation()">
    <div class="py-3 px-6 dark:bg-app-black-100 rounded-t flex items-center justify-between gap-3">
      <h3 class="text-sm font-semibold text-gray-900 dark:text-white">
        <i class="fa-solid fa-pen-to-square text-app-teal-100 mr-2"></i>
        {{ editingIndex === -1 ? 'Add channel' : 'Edit channel #' + ((editingIndex || 0) + 1) }}
      </h3>
      <button type="button" (click)="cancelEdit()"
              class="text-gray-500 hover:text-gray-300 text-sm" aria-label="Close">
        <i class="fa-solid fa-xmark"></i>
      </button>
    </div>

    <div class="flex-1 overflow-y-auto px-6 py-4">
      <div class="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
        <label class="flex flex-col gap-1 md:col-span-1">
          <span class="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
            id *
            <span *ngIf="draftIdError()" class="text-red-500 normal-case ml-1">({{ draftIdError() }})</span>
          </span>
          <input type="text" [(ngModel)]="editingDraft.id"
                 name="draft_channel_id"
                 placeholder="e.g. 1 or as-7"
                 class="bg-gray-50 dark:bg-gray-700 border rounded px-2 py-1 text-xs font-mono dark:text-white"
                 [class.border-red-500]="draftIdError()"
                 [class.border-gray-300]="!draftIdError()"
                 [class.dark:border-gray-600]="!draftIdError()" />
        </label>

        <label class="flex flex-col gap-1 md:col-span-1">
          <span class="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">slug</span>
          <input type="text" [(ngModel)]="editingDraft.slug"
                 name="draft_channel_slug"
                 placeholder="optional friendly slug"
                 class="bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-xs font-mono dark:text-white" />
        </label>

        <label class="flex flex-col gap-1 md:col-span-2">
          <span class="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
            title *
            <span *ngIf="draftTitleError()" class="text-red-500 normal-case ml-1">({{ draftTitleError() }})</span>
          </span>
          <input type="text" [(ngModel)]="editingDraft.title"
                 name="draft_channel_title"
                 placeholder="user-facing display name"
                 class="bg-gray-50 dark:bg-gray-700 border rounded px-2 py-1 text-xs dark:text-white"
                 [class.border-red-500]="draftTitleError()"
                 [class.border-gray-300]="!draftTitleError()"
                 [class.dark:border-gray-600]="!draftTitleError()" />
        </label>
      </div>

      <!-- Role visibility multi-select -->
      <div class="mt-4 border border-gray-200 dark:border-[#434863] rounded p-3 bg-gray-50/40 dark:bg-app-black-200/40">
        <div class="flex items-center gap-2 mb-2">
          <span class="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-medium">
            visible to roles
          </span>
          <span class="text-[10px] text-gray-500 dark:text-gray-400 italic">
            empty = visible to all entitled · non-empty = user.custom:role must match · admins bypass
          </span>
        </div>
        <div class="flex items-center gap-1.5 flex-wrap">
          <button *ngFor="let role of ROLE_OPTIONS"
                  type="button"
                  (click)="toggleDraftRole(role)"
                  class="text-[10px] uppercase tracking-wide font-mono border rounded px-2 py-1 transition-colors"
                  [ngClass]="isDraftRoleSelected(role)
                    ? 'bg-app-teal-100/20 border-app-teal-100 text-app-teal-100'
                    : 'border-gray-300 dark:border-[#434863] text-gray-500 dark:text-gray-500 hover:border-app-teal-100 hover:text-app-teal-100'">
            {{ ROLE_LABELS[role] }}
          </button>
        </div>
      </div>

      <!-- IT-F2-391 cid 52e3671a — per-channel Notification Center settings.
           Lives on sibling Scanner.channel_templates.<channel.id> sub-doc,
           saved via PUT /rest/admin/nc/scanner-config/:scannerId. The three
           knobs below (enabled / schedule.presets / body_html_template) are
           consumed by f2-admin-service NotificationCenterController on
           send/preview. The Stream reference is read-only (Scanner.Stream
           edits still happen on this scanner's main editor, per clar Q2c). -->
      <div class="mt-4 border border-gray-200 dark:border-[#434863] rounded p-3 bg-gray-50/40 dark:bg-app-black-200/40">
        <div class="flex items-center justify-between gap-2 mb-2">
          <span class="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-medium">
            Notification Center settings
          </span>
          <label class="flex items-center gap-1.5 text-[10px] text-gray-700 dark:text-gray-300 cursor-pointer">
            <input type="checkbox" [(ngModel)]="editingDraft.enabled" name="draft_tpl_enabled" />
            Enabled
          </label>
        </div>

        <!-- Per-channel data source (Mike c/5273130f 2026-10-23 — "collection
             selection should be unique for each channel"). Fields map to
             channel.data_source.{db_pattern, collection, requires_schema_translation}.
             Scanner.Stream below is read-only reference for admins who want
             to crib the pattern. -->
        <div class="mb-3 border border-gray-200 dark:border-[#434863] rounded p-2 bg-white/60 dark:bg-app-black-100/60">
          <div class="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-medium mb-2">
            data source
          </div>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
            <label class="flex flex-col gap-1">
              <span class="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                db pattern <span class="normal-case italic text-gray-500">(e.g. <code>F2_LIVE-{{ '{yyyy-MM-dd}' }}</code>)</span>
              </span>
              <input type="text"
                     name="draft_tpl_db_pattern"
                     [ngModel]="editingDraft.data_source?.db_pattern || ''"
                     (ngModelChange)="editingDraft.data_source = editingDraft.data_source || {}; editingDraft.data_source.db_pattern = ($event || '').trim()"
                     placeholder="F2_LIVE-{yyyy-MM-dd}"
                     class="bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-xs font-mono dark:text-white" />
            </label>
            <label class="flex flex-col gap-1">
              <span class="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">collection</span>
              <input type="text"
                     name="draft_tpl_collection"
                     [ngModel]="editingDraft.data_source?.collection || ''"
                     (ngModelChange)="editingDraft.data_source = editingDraft.data_source || {}; editingDraft.data_source.collection = ($event || '').trim()"
                     placeholder="e.g. Core4_Alerts"
                     class="bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-xs font-mono dark:text-white" />
            </label>
          </div>
          <label class="mt-2 flex items-center gap-1.5 text-[11px] text-gray-700 dark:text-gray-300 cursor-pointer select-none">
            <input type="checkbox"
                   name="draft_tpl_require_schema"
                   [ngModel]="editingDraft.data_source?.requires_schema_translation === true"
                   (ngModelChange)="editingDraft.data_source = editingDraft.data_source || {}; editingDraft.data_source.requires_schema_translation = !!$event" />
            Collection uses <code class="mx-1">__schema__</code> sentinel for field translation
            <span class="normal-case italic text-gray-500 dark:text-gray-400 ml-1">(F2 producer convention — compact k-keys)</span>
          </label>

          <!-- IT-F2-391 Mike c/10743951 2026-10-23 — explicit per-doc date
               field for digest-mode day-window filter. Backend parity:
               f2-admin-service PR #264 + NC admin SPA PR #35. Blank =
               fallback to _id-timestamp heuristic (back-compat). -->
          <label class="mt-2 flex flex-col gap-1 text-xs">
            <span class="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
              date field (per-doc) <span class="normal-case italic text-gray-500">— for digest day-window filtering</span>
            </span>
            <input type="text"
                   name="draft_tpl_date_field"
                   [ngModel]="editingDraft.data_source?.date_field || ''"
                   (ngModelChange)="editingDraft.data_source = editingDraft.data_source || {}; editingDraft.data_source.date_field = ($event || '').trim()"
                   placeholder="e.g. closed_date / exit_date / signal_entry_date"
                   class="bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-xs font-mono dark:text-white" />
            <span class="text-[10px] text-gray-500 dark:text-gray-400 italic">
              Used by digest-mode preview + the scheduled dispatcher. When set, backend filters <code>&#123;[date_field]: &#123;$gte, $lt&#125;&#125;</code> to the NY-tz day of the resolved day-offset &mdash; so back-filled rows land in the correct email instead of today's. Blank &rarr; fallback to <code>_id</code>-timestamp heuristic.
            </span>
          </label>

          <!-- IT-F2-391 Mike c/daf5bc26 2026-10-23 — row filter JSON.
               Backend parity: f2-admin-service PR #267 + NC admin SPA
               PR #42. AND-merged into the mongo find() alongside the
               day-window + __schema__ exclusion. `$dateVar` is backend-
               substituted with the resolved NY-tz day ISO string. -->
          <label class="mt-2 flex flex-col gap-1 text-xs">
            <span class="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
              row filter (JSON)
              <span *ngIf="draftFilterError()" class="text-red-500 normal-case ml-1">({{ draftFilterError() }})</span>
              <span *ngIf="!draftFilterError()" class="normal-case italic text-gray-500">— AND-merged with the day window on send-live + dispatch</span>
            </span>
            <textarea name="draft_tpl_filter_json"
                      [(ngModel)]="editingDraftFilterJson"
                      rows="4"
                      spellcheck="false"
                      placeholder='&#123;"signal_entry_date":"$dateVar","status":"ACTIVE"&#125;'
                      class="bg-gray-50 dark:bg-gray-700 border rounded px-2 py-1 text-xs font-mono dark:text-white resize-y"
                      [class.border-red-500]="draftFilterError()"
                      [class.border-gray-300]="!draftFilterError()"
                      [class.dark:border-gray-600]="!draftFilterError()">
            </textarea>
            <span class="text-[10px] text-gray-500 dark:text-gray-400 italic">
              Match whatever filter the Scanner UI already applies so the email row set lines up with what members see. Use the literal string <code>$dateVar</code> as the value for any field that should get substituted with the resolved day's NY-tz ISO date (e.g. <code>&#123;"close_date":"$dateVar","status":"CLOSED"&#125;</code> for exit alerts). The <code>_id</code> key is reserved by the backend to keep the <code>__schema__</code> sentinel exclusion intact. Blank &rarr; day window alone (every row in the collection for the day).
            </span>
          </label>

          <!-- Read-only Scanner.Stream reference (Mike clar Q2c) — useful
               for admins cribbing the SourceDbPattern + Collection pair. -->
          <div *ngIf="streamSnapshot.count > 0" class="mt-2 border-t border-gray-200 dark:border-[#434863] pt-2">
            <div class="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-medium mb-1">
              Scanner.Stream reference <span class="normal-case italic text-gray-500 dark:text-gray-400">(read-only)</span>
            </div>
            <div class="text-[11px] font-mono text-gray-700 dark:text-gray-300 leading-5">
              <div><span class="text-gray-500">SourceDbPattern:</span> {{ streamSnapshot.source_db_pattern || '—' }}</div>
              <div><span class="text-gray-500">Collection:</span> {{ streamSnapshot.collection || '—' }}</div>
              <div><span class="text-gray-500">KeyField:</span> {{ streamSnapshot.key_field || '—' }}</div>
              <div *ngIf="streamSnapshot.count > 1" class="text-amber-500 mt-1 normal-case">
                {{ streamSnapshot.count }} Stream entries on this scanner — showing the first; edit on the Scanner form to change the primary.
              </div>
            </div>
          </div>
        </div>

        <!-- Schedule preset multi-select (Mike clar Q1a). -->
        <div class="mb-3">
          <div class="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-medium mb-1">
            send cadence
          </div>
          <div class="flex items-start gap-1.5 flex-wrap">
            <button *ngFor="let p of SCHEDULE_PRESETS"
                    type="button"
                    (click)="toggleDraftPreset(p.id)"
                    [title]="p.hint"
                    class="text-[10px] tracking-wide border rounded px-2 py-1 transition-colors"
                    [ngClass]="isDraftPresetSelected(p.id)
                      ? 'bg-app-teal-100/20 border-app-teal-100 text-app-teal-100'
                      : 'border-gray-300 dark:border-[#434863] text-gray-500 dark:text-gray-500 hover:border-app-teal-100 hover:text-app-teal-100'">
              {{ p.label }}
            </button>
          </div>
          <div *ngIf="draftCustomCronEnabled" class="mt-2">
            <label class="flex flex-col gap-1 text-xs">
              <span class="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">custom cron (5-field)</span>
              <input type="text"
                     name="draft_tpl_cron"
                     [(ngModel)]="editingDraft.schedule!.cron"
                     placeholder="e.g. 30 16 * * 1-5"
                     class="bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-xs font-mono dark:text-white" />
            </label>
          </div>
          <p class="mt-1 text-[10px] text-gray-500 dark:text-gray-400 italic">
            Multi-select; several presets can fire the same channel on different schedules. Custom cron overrides when selected.
          </p>
        </div>

        <!-- Template body (HTML). Phase A — plain textarea; a WYSIWYG
             preview is a follow-up. Backend POST /admin/nc/template-preview
             is the render path when we add the Preview button. -->
        <div>
          <label class="flex flex-col gap-1 text-xs">
            <span class="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
              email body (HTML template)
            </span>
            <textarea name="draft_tpl_body"
                      [(ngModel)]="editingDraft.body_html_template"
                      rows="7"
                      placeholder="&lt;p&gt;Hi {{ '{{' }}first_name{{ '}}' }}, your scanner fired {{ '{{' }}event_count{{ '}}' }} new alerts…&lt;/p&gt;"
                      class="bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-xs font-mono dark:text-white resize-y">
            </textarea>
            <span class="text-[10px] text-gray-500 dark:text-gray-400 italic">
              Rendered by f2-admin-service NotificationCenterController (template-preview + send paths). Blank → f2-admin-service falls through to the brand default. Available tokens are documented alongside <code>/admin/nc/template-preview</code>.
            </span>
          </label>
        </div>
      </div>
    </div>

    <div class="py-3 px-6 border-t border-gray-200 dark:border-[#434863] flex items-center gap-2">
      <button *ngIf="editingIndex !== -1"
              type="button" (click)="deleteFromEdit()"
              class="text-xs text-red-500 hover:text-red-400 border border-red-500/50 hover:border-red-500 rounded px-3 py-1 flex items-center gap-1"
              title="Remove this channel">
        <i class="fa-solid fa-trash text-[10px]"></i>
        Delete
      </button>

      <!-- IT-F2-391 Mike c/2bf04074 2026-10-23 — "the channel settings panels
           should have copy and paste buttons". Copies the current draft's
           non-identity settings (NC enabled/template/schedule/data_source +
           role chips) to the clipboard as JSON; Paste reads the clipboard
           back onto the same draft, keeping id/slug/title intact so the
           channel's identity doesn't get clobbered. Save-flow unchanged. -->
      <button type="button" (click)="copyDraftSettings()"
              [disabled]="copyBusy"
              class="text-xs text-gray-300 border border-gray-300 dark:border-[#434863] hover:border-app-teal-100 hover:text-app-teal-100 rounded px-3 py-1 flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
              [title]="'Copy this channel\'s settings (enabled / template / schedule / data_source / roles) to clipboard as JSON'">
        <i class="fa-solid fa-copy text-[10px]"></i>
        <span>{{ copyBusy === 'done' ? 'Copied' : 'Copy' }}</span>
      </button>
      <button type="button" (click)="pasteDraftSettings()"
              [disabled]="pasteBusy"
              class="text-xs text-gray-300 border border-gray-300 dark:border-[#434863] hover:border-app-teal-100 hover:text-app-teal-100 rounded px-3 py-1 flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
              [title]="'Overlay settings from clipboard JSON onto this channel (keeps id / slug / title)'">
        <i class="fa-solid fa-paste text-[10px]"></i>
        <span>{{ pasteBusy === 'done' ? 'Pasted' : (pasteBusy === 'error' ? 'Bad JSON' : 'Paste') }}</span>
      </button>

      <div class="flex-1"></div>
      <button type="button" (click)="cancelEdit()"
              class="dark-btn-secondary text-xs px-3 py-1">cancel</button>
      <button type="button" (click)="saveEdit()"
              [disabled]="!canSaveDraft()"
              class="dark-btn-primary text-xs px-3 py-1 disabled:opacity-50 disabled:cursor-not-allowed">
        {{ editingIndex === -1 ? 'Add' : 'Save' }}
      </button>
    </div>
  </div>
</div>
